import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, Trash2, Globe2, Star, Eye, ArrowUpRight, ArrowDownRight } from "lucide-react";
import { apiGet, apiPost, apiDelete } from "@/lib/api";
import { toast } from "sonner";

const fmtUsd = (v) => v == null ? "—" : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtPct = (v, d = 1) => v == null || isNaN(v) ? "—" : `${Number(v).toFixed(d)}%`;

const SIGNAL_CLASS = {
  STRONG_BUY: "bg-emerald-600 text-white",
  BUY: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30",
  WATCH: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-500/30",
  HOLD: "bg-muted text-muted-foreground",
  AVOID: "bg-destructive/15 text-destructive border border-destructive/30",
};

const VAL_META = {
  "Deep Value":   { cls: "bg-[#14532d] text-white border-[#14532d]",  icon: "💎" },
  "Undervalued":  { cls: "bg-[#16a34a] text-white border-[#16a34a]",  icon: "📉" },
  "Fair Value":   { cls: "bg-[#6b7280] text-white border-[#6b7280]",  icon: "⚖" },
  "Overvalued":   { cls: "bg-[#ea580c] text-white border-[#ea580c]",  icon: "📈" },
  "Overpriced":   { cls: "bg-[#dc2626] text-white border-[#dc2626]",  icon: "🔴" },
};

const UsWatchlistPanel = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [newSymbol, setNewSymbol] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await apiGet("/us-watchlist");
      setItems(d?.items || []);
    } catch (e) {
      // fallthrough to empty
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e?.preventDefault();
    const sym = (newSymbol || "").trim().toUpperCase();
    if (!sym) return;
    setAdding(true);
    try {
      const r = await apiPost("/us-watchlist", { us_symbol: sym });
      if (r.status === "already_added") {
        toast.info(`${sym} is already in your US watchlist.`);
      } else {
        toast.success(`Added ${sym} to US watchlist.`);
      }
      setNewSymbol("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add");
    } finally {
      setAdding(false);
    }
  };

  const remove = async (sym) => {
    if (!window.confirm(`Remove ${sym} from US watchlist?`)) return;
    try {
      await apiDelete(`/us-watchlist/${encodeURIComponent(sym)}`);
      toast.success(`Removed ${sym}.`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to remove");
    }
  };

  return (
    <div className="space-y-4" data-testid="us-watchlist-panel">
      {/* Add bar */}
      <Card>
        <CardContent className="pt-4 pb-4">
          <form onSubmit={add} className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[14rem] space-y-1">
              <label className="text-xs text-muted-foreground">Add a US symbol</label>
              <Input
                placeholder="e.g. AAPL, MSFT, JNJ…"
                value={newSymbol}
                onChange={(e) => setNewSymbol(e.target.value.toUpperCase())}
                data-testid="us-watchlist-add-input"
                autoComplete="off"
              />
            </div>
            <Button type="submit" disabled={adding || !newSymbol.trim()} data-testid="us-watchlist-add-btn">
              <Plus className="h-4 w-4 mr-1.5" /> {adding ? "Adding…" : "Add"}
            </Button>
            <span className="text-xs text-muted-foreground ml-auto">
              All amounts in USD · Tracks signal & valuation changes
            </span>
          </form>
        </CardContent>
      </Card>

      {/* Items */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2"><Star className="h-4 w-4" /> US Watchlist <span className="text-muted-foreground font-normal">({items.length})</span></CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-2">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-9 w-full" />)}</div>
          ) : items.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground" data-testid="us-watchlist-empty">
              <Globe2 className="h-8 w-8 mx-auto mb-2 text-muted-foreground/60" />
              No US stocks in your watchlist yet. Add one above to track signal changes.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Symbol</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Sector</TableHead>
                    <TableHead className="text-right">Price</TableHead>
                    <TableHead className="text-right">Score</TableHead>
                    <TableHead>Signal</TableHead>
                    <TableHead>Valuation</TableHead>
                    <TableHead className="text-right">Target</TableHead>
                    <TableHead className="text-right">Upside</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((it) => {
                    const upClass =
                      it.upside_pct > 0 ? "text-emerald-600 dark:text-emerald-400" :
                      it.upside_pct < 0 ? "text-destructive" : "text-muted-foreground";
                    const Arrow = it.upside_pct > 0 ? ArrowUpRight : ArrowDownRight;
                    return (
                      <TableRow key={it.id} data-testid={`us-watchlist-row-${it.us_symbol}`}>
                        <TableCell>
                          <Link to={`/us-stocks/${encodeURIComponent(it.us_symbol)}`} className="font-mono font-semibold text-primary hover:underline">
                            {it.us_symbol}
                          </Link>
                        </TableCell>
                        <TableCell className="text-xs max-w-[14rem] truncate">{it.company_name}</TableCell>
                        <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{it.sector}</TableCell>
                        <TableCell className="text-right tabular-nums">{fmtUsd(it.current_price)}</TableCell>
                        <TableCell className="text-right tabular-nums font-medium">{it.composite_score?.toFixed(1) ?? "—"}</TableCell>
                        <TableCell>
                          {it.signal ? (
                            <Badge className={`${SIGNAL_CLASS[it.signal] || ""} text-[10px]`}>
                              {it.signal.replace("_", " ")}
                            </Badge>
                          ) : <span className="text-xs text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell>
                          {it.valuation_status ? (() => {
                            const m = VAL_META[it.valuation_status];
                            if (!m) return <Badge variant="outline" className="text-[10px]">{it.valuation_status}</Badge>;
                            return (
                              <Badge variant="outline" className={`${m.cls} text-[10px] font-medium whitespace-nowrap inline-flex items-center gap-1 px-2 py-0.5`}>
                                <span aria-hidden="true" className="leading-none">{m.icon}</span>
                                {it.valuation_status}
                              </Badge>
                            );
                          })() : <span className="text-xs text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{fmtUsd(it.base_target_price)}</TableCell>
                        <TableCell className={`text-right tabular-nums ${upClass}`}>
                          {it.upside_pct == null ? "—" : (
                            <span className="inline-flex items-center gap-0.5 whitespace-nowrap">
                              <Arrow className="h-3 w-3" />
                              {Math.abs(it.upside_pct).toFixed(1)}%
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="space-x-1">
                          <Button asChild size="icon" variant="ghost" className="h-7 w-7">
                            <Link to={`/us-stocks/${encodeURIComponent(it.us_symbol)}`} aria-label={`View ${it.us_symbol}`}><Eye className="h-3.5 w-3.5" /></Link>
                          </Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => remove(it.us_symbol)} data-testid={`us-watchlist-remove-${it.us_symbol}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default UsWatchlistPanel;
